import React, { useEffect, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Image as ImageIcon, X as XIcon, Save } from "lucide-react";
import { useAppState } from "@/state/AppStateProvider";
import { brandApi, assetsApi, mediaUrl } from "@/lib/apiClient";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/**
 * Per-post Instagram card control, used at review time on both the Create
 * post-editor and the Library/post detail. Three modes:
 *   Auto   — a designed look, varied per post (ignores the brand card settings)
 *   Brand  — this brand's saved card defaults
 *   Manual — pick Style (layout), Theme, Brand color, Format for THIS post
 * Persists to asset.card_style via updateAsset; the publisher renders exactly this.
 *
 * `variant`: "dark" (Create editor, on a dark panel) | "light" (Library).
 */
export function IgCardControl({ asset, updateAsset, variant = "dark" }) {
  const { state } = useAppState();
  const cs = asset.card_style || {};
  const [mode, setMode] = useState(cs.mode || "brand");
  const [manual, setManual] = useState({
    theme: cs.theme || "auto",
    card_color: cs.card_color || "",
    card_layout: cs.card_layout || "auto",
    card_format: cs.card_format || "single",
  });
  const [themes, setThemes] = useState([]);
  const [layouts, setLayouts] = useState([{ id: "auto", label: "Auto — varies per post" }]);
  const [previewUrl, setPreviewUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const dark = variant === "dark";

  const brandProfile = useMemo(() => {
    const b = (state.brands || []).find((x) => x.id === state.activeBrandId) || (state.brands || [])[0];
    return b?.profile || {};
  }, [state.brands, state.activeBrandId]);

  useEffect(() => { brandApi.cardThemes().then((r) => { if (r?.themes) setThemes(r.themes); if (r?.layouts) setLayouts(r.layouts); }).catch(() => {}); }, []);

  const effective = () => {
    const bp = brandProfile;
    if (mode === "auto") return { theme: null, layout: null, brand_color: null, card_format: bp.card_format || "single" };
    if (mode === "brand") return {
      theme: bp.card_theme && bp.card_theme !== "auto" ? bp.card_theme : null,
      layout: bp.card_layout && bp.card_layout !== "auto" ? bp.card_layout : null,
      brand_color: bp.card_color || null,
      card_format: bp.card_format || "single",
    };
    return {
      theme: manual.theme !== "auto" ? manual.theme : null,
      layout: manual.card_layout !== "auto" ? manual.card_layout : null,
      brand_color: manual.card_color || null,
      card_format: manual.card_format || "single",
    };
  };

  const preview = async () => {
    setBusy(true);
    try {
      const r = await brandApi.cardPreview({ text: asset.hook || asset.title || "Your hook here", ratio: brandProfile.card_ratio || "portrait", ...effective() });
      if (r?.url) setPreviewUrl(mediaUrl(r.url) + `?t=${Date.now()}`);
    } catch (_) { toast.error("Couldn't render the card preview"); } finally { setBusy(false); }
  };
  // Auto-refresh the preview when the mode changes (not on every keystroke).
  useEffect(() => {
    preview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Edits are a local draft until "Save card" — so the user has an explicit
  // commit (and dirty state) rather than silent auto-save.
  const saved = asset.card_style || {};
  const dirty = mode !== (saved.mode || "brand")
    || (mode === "manual" && (
      manual.theme !== (saved.theme || "auto")
      || manual.card_color !== (saved.card_color || "")
      || manual.card_layout !== (saved.card_layout || "auto")
      || manual.card_format !== (saved.card_format || "single")));
  const changeMode = (m) => setMode(m);
  const setMan = (k, v) => setManual((mm) => ({ ...mm, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const card_style = mode === "manual" ? { mode: "manual", ...manual } : { mode };
      // Persist the choice first so the real-render endpoint reads it.
      await assetsApi.patch(asset.id, { card_style });
      // Render the ACTUAL card(s) this asset will publish, and mirror slide 1 to
      // cover_image so the post preview shows exactly what posts.
      const r = await assetsApi.cards(asset.id);
      const url = r?.urls?.length ? mediaUrl(r.urls[0]) : null;
      updateAsset(asset.id, url ? { card_style, cover_image: url } : { card_style });
      if (url) setPreviewUrl(url + `?t=${Date.now()}`);
      toast.success("Card saved — this is exactly what will publish.");
    } catch (e) {
      toast.error(`Couldn't save the card: ${e.message}`);
    } finally { setBusy(false); }
  };

  const triggerCls = dark ? "border-white/15 bg-white/10 text-white" : undefined;
  const panelCls = dark ? "border-white/10 bg-white/[0.03]" : "";
  const labelCls = dark ? "text-slate-400" : "text-muted-foreground";
  const hintCls = dark ? "text-slate-400" : "text-muted-foreground";

  return (
    <div className={cn("space-y-2 rounded-lg border p-2.5", panelCls)} style={dark ? undefined : { borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-1)" }} data-testid="igcard-control">
      <Label className={cn("text-[11px] uppercase tracking-[0.16em]", labelCls)}>Instagram card</Label>
      <div className="grid grid-cols-3 gap-1.5">
        {[["auto", "Auto"], ["brand", "Brand"], ["manual", "Manual"]].map(([id, label]) => {
          const on = mode === id;
          return (
            <button key={id} type="button" onClick={() => changeMode(id)} data-testid={`igcard-mode-${id}`}
              className={cn("rounded-md border px-2 py-1.5 text-[12px] font-medium transition-colors",
                on
                  ? "border-[color:var(--accent-blue)] " + (dark ? "bg-[color:var(--accent-blue)]/20 text-[color:var(--accent-blue-ink)]" : "bg-[color:var(--surface-2)] text-1")
                  : dark ? "border-white/10 bg-white/5 text-slate-300 hover:text-white" : "text-muted-foreground hover:text-1")}
              style={on || dark ? undefined : { borderColor: "var(--border-subtle)" }}>
              {label}
            </button>
          );
        })}
      </div>
      <p className={cn("text-[11px]", hintCls)}>
        {mode === "auto" ? "A designed look, varied per post." : mode === "brand" ? "Uses this brand's saved card settings." : "Choose the look for just this post."}
      </p>

      {mode === "manual" && (
        <div className="grid grid-cols-2 gap-1.5">
          <Select value={manual.card_layout} onValueChange={(v) => setMan("card_layout", v)}>
            <SelectTrigger className={triggerCls} data-testid="igcard-layout"><SelectValue placeholder="Style" /></SelectTrigger>
            <SelectContent>{layouts.map((l) => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={manual.theme} onValueChange={(v) => setMan("theme", v)}>
            <SelectTrigger className={triggerCls} data-testid="igcard-theme"><SelectValue placeholder="Theme" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto theme</SelectItem>
              {themes.map((t) => <SelectItem key={t.id} value={t.id}><span className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ backgroundColor: t.accent }} /> <span className="capitalize">{t.id}</span></span></SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={manual.card_format} onValueChange={(v) => setMan("card_format", v)}>
            <SelectTrigger className={triggerCls} data-testid="igcard-format"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="single">Single</SelectItem><SelectItem value="carousel">Carousel</SelectItem></SelectContent>
          </Select>
          <div className="flex items-center gap-1.5">
            <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(manual.card_color) ? manual.card_color : "#8FE04A"} onChange={(e) => setMan("card_color", e.target.value)} className={cn("h-9 w-9 shrink-0 cursor-pointer rounded border bg-transparent p-0.5", dark ? "border-white/15" : "")} style={dark ? undefined : { borderColor: "var(--border-subtle)" }} aria-label="brand color" data-testid="igcard-color" />
            {manual.card_color && <button type="button" onClick={() => setMan("card_color", "")} className={dark ? "text-slate-400 hover:text-red-300" : "text-muted-foreground hover:text-[color:var(--accent-red)]"}><XIcon className="h-3.5 w-3.5" /></button>}
          </div>
        </div>
      )}

      {previewUrl && <img src={previewUrl} alt="card preview" className={cn("mt-1 max-h-64 rounded-lg border", dark ? "border-white/10" : "")} style={dark ? undefined : { borderColor: "var(--border-subtle)" }} />}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" className={cn("gap-1.5", dark ? "border-white/15 bg-white/10 text-white hover:bg-white/20" : "")} onClick={preview} disabled={busy} data-testid="igcard-preview">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />} Preview card
        </Button>
        <Button type="button" size="sm" className="gap-1.5" onClick={save} disabled={busy} data-testid="igcard-save" style={{ backgroundColor: "var(--accent-blue)", color: "#FFFFFF" }}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save card
        </Button>
        {dirty && <span className="text-[11px] text-amber-500">Unsaved changes</span>}
      </div>
    </div>
  );
}
